import React from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./AlertDialog.module.scss";

export interface AlertDialogContentComponentProps
  extends AlertDialogPrimitive.AlertDialogContentProps,
    React.RefAttributes<HTMLDivElement> {
  title: string;
  description: React.ReactNode;
  actions: React.ReactNode;
  status?: "error" | "success" | "warning" | "info" | "default";
  onClose?: () => void;
}

const statusIconMap = {
  error: IconNameEnum.warning,
  success: IconNameEnum.checkFilled,
  warning: IconNameEnum.warning,
  info: IconNameEnum.info,
  default: undefined,
};

const COMPONENT_KEY = CoreUiComponents.AlertDialog;

export const AlertDialogContent = React.forwardRef<
  HTMLDivElement,
  AlertDialogContentComponentProps
>(({ children, ...props }, forwardedRef) => {
  const {
    title,
    className,
    description,
    actions,
    status = "default",
    onClose,
  } = props;

  const { t } = useI18nContext();
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, { [status]: true });

  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className={cn("overlay")} />
      <AlertDialogPrimitive.Content
        ref={forwardedRef}
        className={clsx(block, cn("root"), className)}
      >
        <AlertDialogPrimitive.Title className={cn("title")}>
          {status !== "default" && (
            <Icon
              iconName={statusIconMap[status]}
              className={cn("icon", [status])}
            />
          )}
          {title}
          <AlertDialogPrimitive.Cancel asChild>
            <button
              className={cn("close")}
              onClick={onClose}
              aria-label={t("close.button")}
            >
              <Icon
                iconName={IconNameEnum.close}
                className={cn("close-icon")}
              />
            </button>
          </AlertDialogPrimitive.Cancel>
        </AlertDialogPrimitive.Title>

        <div className={cn("content")}>
          <AlertDialogPrimitive.Description className={cn("description")}>
            {description}
          </AlertDialogPrimitive.Description>
          <div className={cn("actions")}>{actions}</div>
          {children}
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});
